// src/components/admin/Reviews.tsx
/* eslint import/no-unresolved: 0 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

/* ===== API ベース ===== */
// ENV にベースURLがあれば使う（例: http://localhost:4000）
// なければブラウザからは相対で /api/admin を叩く
const RAW_API_BASE: string =
  (process.env.REACT_APP_API_BASE_URL as string) ||
  ((import.meta as any)?.env?.VITE_API_BASE_URL as string) ||
  "";

const API_ADMIN = RAW_API_BASE
  ? `${RAW_API_BASE.replace(/\/+$/, "")}/api/admin`
  : "/api/admin";

/* ===== 型 ===== */
type ReviewRow = {
  id: number | string;
  planName?: string;
  createdAt?: string;
  userName?: string;
  gender?: string;
  rating?: number;
  recommend?: number;
  comment?: string;
  status?: string; // "公開" | "非公開" 想定
};

/* ===== 1ページあたり件数 ===== */
const PER_PAGE = 10;

export default function Reviews() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // ページング
  const [page, setPage] = useState(1);

  // ===== 一覧取得 =====
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        setLoading(true);
        setErr(null);

        const res = await fetch(`${API_ADMIN}/reviews`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (abort) return;

        const list = Array.isArray(data) ? data : data?.items ?? [];
        const normalized: ReviewRow[] = (list as any[]).map((x, i) => ({
          id: x.id ?? i + 1,
          planName: x.planName ?? x.plan ?? "",
          createdAt: x.createdAt ?? x.date ?? x.postedAt ?? "",
          userName: x.userName ?? x.user ?? x.author ?? "",
          gender: x.gender ?? "",
          rating: Number(x.rating ?? x.level ?? 0),
          recommend: Number(x.recommend ?? x.rating ?? x.level ?? 0),
          comment: x.comment ?? "",
          status: x.status ?? "公開",
        }));

        // 新着順
        normalized.sort((a, b) => {
          const ta = new Date(a.createdAt || 0).getTime();
          const tb = new Date(b.createdAt || 0).getTime();
          return tb - ta;
        });

        setRows(normalized);
        setPage(1); // 読み直し時は1ページ目へ
      } catch (e: any) {
        setErr(e?.message || "レビュー一覧の取得に失敗しました");
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, []);

  // 総ページ数
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(rows.length / PER_PAGE)),
    [rows.length]
  );

  // rows が減って現在ページがはみ出したときに補正
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  // 現在ページに表示する行
  const pageRows = useMemo(() => {
    const start = (page - 1) * PER_PAGE;
    return rows.slice(start, start + PER_PAGE);
  }, [rows, page]);

  // ===== 削除 =====
  const handleDelete = async (id: number | string) => {
    if (!window.confirm("本当に削除しますか？")) return;
    try {
      setErr(null);
      const res = await fetch(`${API_ADMIN}/reviews/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (e: any) {
      setErr(e?.message || "削除に失敗しました");
    }
  };

  // ===== 公開 / 非公開トグル =====
  const toggleStatus = async (row: ReviewRow) => {
    const id = row.id;
    const next = row.status === "公開" ? "非公開" : "公開";
    try {
      setErr(null);
      const body = {
        planName: row.planName ?? "",
        createdAt: row.createdAt ?? "",
        userName: row.userName ?? "",
        gender: row.gender ?? "",
        rating: row.rating ?? 0,
        recommend: row.recommend ?? row.rating ?? 0,
        comment: row.comment ?? "",
        status: next,
      };
      const res = await fetch(`${API_ADMIN}/reviews/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status: next } : r))
      );
    } catch (e: any) {
      setErr(e?.message || "状態変更に失敗しました");
    }
  };

  const stars = (n = 0) =>
    "★★★★★".slice(0, Math.max(0, Math.min(5, n)));

  const fmtDate = (s?: string) => {
    if (!s) return "";
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const hasRows = useMemo(() => rows.length > 0, [rows]);

  return (
    <div style={{ maxWidth: 1000, margin: "40px auto" }}>
      <h2 style={{ marginBottom: 24 }}>📝 レビュー管理</h2>

      {err && (
        <div
          style={{
            marginBottom: 12,
            padding: 8,
            borderRadius: 6,
            background: "#ffebee",
            color: "#c62828",
          }}
        >
          {err}
        </div>
      )}

      <button
        onClick={() => navigate("/admin/reviews/edit/new")}
        style={{
          background: "#1976d2",
          color: "#fff",
          padding: "8px 20px",
          border: "none",
          borderRadius: 5,
          marginBottom: 24,
          cursor: "pointer",
        }}
      >
        ＋新規レビュー登録
      </button>

      <table
        style={{
          width: "100%",
          borderCollapse: "separate",
          borderSpacing: 0,
          background: "#fff",
          boxShadow: "0 0 8px #eee",
          tableLayout: "fixed", // ★ 追加：列幅を安定させる
        }}
      >
        <thead>
          <tr style={{ background: "#e6e9ee", fontWeight: "bold" }}>
            <th style={{ padding: 10, width: 80 }}>ID</th>
            <th style={{ width: 140 }}>購入プラン</th>
            <th style={{ width: 110 }}>投稿日時</th>
            <th style={{ width: 110 }}>投稿者</th>
            <th style={{ width: 70 }}>性別</th>
            <th style={{ width: 90 }}>おすすめ</th>
            <th>コメント</th>
            <th style={{ width: 80 }}>状態</th>
            <th style={{ width: 120 }}>操作</th>
          </tr>
        </thead>
        <tbody>
          {!loading &&
            hasRows &&
            pageRows.map((r) => (
              <tr key={r.id} style={{ borderBottom: "1px solid #eee" }}>
                {/* ID は長いので省略表示＆ホバーで全文 */}
                <td
                  style={{
                    textAlign: "left",
                    padding: "6px 4px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={String(r.id)}
                >
                  {r.id}
                </td>
                <td style={{ padding: "6px 4px" }}>{r.planName}</td>
                <td style={{ padding: "6px 4px" }}>{fmtDate(r.createdAt)}</td>
                <td style={{ padding: "6px 4px" }}>{r.userName}</td>
                <td style={{ padding: "6px 4px" }}>{r.gender}</td>
                <td style={{ padding: "6px 4px" }}>
                  {stars(r.recommend ?? r.rating ?? 0)}
                </td>
                {/* コメントは折り返しOK */}
                <td
                  style={{
                    padding: "6px 4px",
                    wordBreak: "break-all",
                  }}
                >
                  {r.comment}
                </td>
                <td style={{ padding: "6px 4px" }}>
                  <button
                    onClick={() => toggleStatus(r)}
                    style={{
                      background: r.status === "公開" ? "#4CAF50" : "#aaa",
                      color: "#fff",
                      border: "none",
                      borderRadius: 4,
                      padding: "4px 10px",
                      cursor: "pointer",
                    }}
                  >
                    {r.status || "公開"}
                  </button>
                </td>
                <td style={{ padding: "6px 4px" }}>
                  <button
                    onClick={() =>
                      navigate(`/admin/reviews/edit/${r.id}`)
                    }
                    style={{
                      padding: "4px 10px",
                      marginRight: 8,
                      border: "1px solid #888",
                      borderRadius: 4,
                      cursor: "pointer",
                    }}
                  >
                    編集
                  </button>
                  <button
                    onClick={() => handleDelete(r.id)}
                    style={{
                      padding: "4px 10px",
                      background: "#e53935",
                      color: "#fff",
                      border: "none",
                      borderRadius: 4,
                      cursor: "pointer",
                    }}
                  >
                    削除
                  </button>
                </td>
              </tr>
            ))}


          {!loading && !hasRows && (
            <tr>
              <td colSpan={9} style={{ padding: 16, textAlign: "center" }}>
                レビューはまだ登録されていません。
              </td>
            </tr>
          )}

          {loading && (
            <tr>
              <td colSpan={9} style={{ padding: 16, textAlign: "center" }}>
                読み込み中…
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* ページネーション */}
      {!loading && hasRows && (
        <div
          style={{
            marginTop: 12,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 12,
          }}
        >
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              border: "1px solid #999",
              background: page <= 1 ? "#f5f5f5" : "#fff",
              cursor: page <= 1 ? "default" : "pointer",
            }}
          >
            前へ
          </button>
          <span style={{ fontSize: 13 }}>
            {page} / {totalPages} ページ
          </span>
          <button
            type="button"
            onClick={() =>
              setPage((p) => Math.min(totalPages, p + 1))
            }
            disabled={page >= totalPages}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              border: "1px solid #999",
              background: page >= totalPages ? "#f5f5f5" : "#fff",
              cursor: page >= totalPages ? "default" : "pointer",
            }}
          >
            次へ
          </button>
        </div>
      )}
    </div>
  );
}
